import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/DataTableContainer"
import DashboardLoader from "components/Loader/DashboardLoader"
import DeleteModal from "pages/Calendar/DeleteModal"
import React, { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { selectUser } from "store/features/auth/authSlice"
import {
  useGetAllInvoicesQuery,
  useDeleteInvoiceMutation,
} from "store/features/invoice/invoiceApi"

const AllInvoices = () => {
  const user = useSelector(selectUser)

  const { data: invoices, isLoading } = useGetAllInvoicesQuery()
  const [deleteInvoice, { isLoading: deletingInvoice }] =
    useDeleteInvoiceMutation()

  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null)

  const showDeleteModal = id => {
    setSelectedInvoiceId(id)
    setDeleteModal(true)
  }

  const handleDeleteInvoice = async () => {
    try {
      await deleteInvoice(selectedInvoiceId).unwrap()
      toast.success("Invoice deleted successfully")
      setDeleteModal(false)
    } catch (error) {
      toast.error("Failed to delete invoice")
    }
  }

  // Get client name from invoice data
  const getClientName = invoiceData => {
    return invoiceData?.clientInfo?.companyName || "N/A"
  }

  // Get total amount from calculations
  const getTotalAmount = calculations => {
    return calculations?.total || 0
  }

  // Format date
  const formatDate = dateString => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  // Add this helper function to get received amount
  const getReceivedAmount = calculations => {
    return calculations?.received || 0
  }

  // Add this helper function to get outstanding amount
  const getOutstandingAmount = calculations => {
    return calculations?.outstanding || 0
  }

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: "Invoice No",
        accessorKey: "invoice_no",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{row.original.invoice_no}</div>
          </div>
        ),
      },
      {
        header: "Client Name",
        accessorKey: "invoice_data",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getClientName(row.original.invoice_data),
      },
      {
        header: "Total Amount",
        accessorKey: "calculations",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const total = getTotalAmount(row.original.calculations)
          const currency = row.original.calculations.currency
          return (
            <span>
              {currency || "BDT"} {total.toLocaleString()}
            </span>
          )
        },
      },
      {
        header: "Received Amount",
        accessorKey: "calculations",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const received = getReceivedAmount(row.original.calculations)
          const currency = row.original.calculations.currency

          return (
            <span className="text-success">
              {currency || "BDT"} {received.toLocaleString()}
            </span>
          )
        },
      },
      {
        header: "Remaining Amount",
        accessorKey: "calculations",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const outstanding = getOutstandingAmount(row.original.calculations)
          const currency = row.original.calculations.currency

          return (
            <span className="text-danger">
              {currency || "BDT"} {outstanding.toLocaleString()}
            </span>
          )
        },
      },

      {
        header: "Sent To",
        accessorKey: "sent_to",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.sent_to || "N/A",
      },
      {
        header: "Sent Date",
        accessorKey: "sent_at",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.sent_at),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <Link
              to={`/invoice/${row.original.id}`}
              className="btn btn-info btn-sm"
            >
              <i className="fas fa-eye me-1"></i> View
            </Link>

            {(user?.role === "super_admin" || user?.role === "admin") && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => showDeleteModal(row.original.id)}
              >
                <i className="fas fa-trash me-1"></i>
                {deletingInvoice ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
        ),
      },
    ]

    return baseColumns
  }, [deletingInvoice, user])

  if (isLoading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Invoices" breadcrumbItem="All Invoices" />

        <TableContainer
          columns={columns}
          data={invoices || []}
          isGlobalFilter={true}
          isPagination={true}
          SearchPlaceholder="Search Invoices..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteInvoice}
          loader={deletingInvoice}
          onCloseClick={() => setDeleteModal(false)}
        />
      </div>
    </div>
  )
}

export default AllInvoices
